import { InProgressMessage, Intent, PersistentMessage } from "@/types/chat"
import { MapMarker } from "@/types/map-marker"
import { PlaceSummary } from "@/types/place"
import { createSelector } from "@reduxjs/toolkit"
import isEmpty from "lodash/isEmpty"
import uniqBy from "lodash/uniqBy"
import { shallowEqual } from "react-redux"

import { present } from "@/lib/utils/presence"
import {
  collectWebSearchResultsFromMessage,
  FOR_YOU_WEB_EVENT_SOURCE,
  messageHasWebSearchResultsForMap,
  WebSearchResultForMap,
} from "@/lib/web-search-results-for-map"
import { filterOutlierEntities } from "../chat-utils"
import { isAirport, isLocation } from "../entity-utils"
import { isCancelled, isPlaceItineraryEvent } from "../itinerary-utils"
import {
  isPlaceBasedMarker,
  itineraryEventToMapMarker,
  mapMarkerEntityRef,
  placeToMapMarker,
} from "../map-marker-utils"
import { forYouApi } from "./api/for-you-api"
import { inspirationApi } from "./api/inspiration-api"
import {
  selectEmbedded,
  selectIsSplitView,
  selectOrganizationExamples,
} from "./app-slice"
import { selectChatAreaPlaces } from "./chat-explore-area-slice"
import {
  FilteredPlaceType,
  selectFilteredPlaceType,
  selectHighlightedMessage,
  selectHighlightedPlaces,
  selectShowSmallUIMap,
} from "./chat-mode-slice"
import {
  selectIsStreaming as selectExploreIsStreaming,
  selectHomeLocation,
  selectIsAutoDetectingLocation,
} from "./explore-slice"
import { selectIsGeoAcquiring } from "./geolocation-selectors"
import {
  selectAddedAndSavedPlaces,
  selectAddedPlaces,
  selectItinerary,
} from "./itinerary-slice"
import type { RootState } from "./redux-store"
import {
  selectTripDestination,
  selectTripHasDestination,
  selectTripIsUserCreated,
  selectTripMajorStopsDetails,
  selectTripSavedPlaces,
} from "./trip-slice"
import {
  selectFirstLatestMessagePlace,
  selectHasInProgressMessage,
  selectHasPersistentMessages,
  selectInProgressMessage,
  selectLastBotMessageTypesOfInterest,
  selectLastIntentCode,
  selectLatestMessagePlaceCount,
  selectLatestNonFollowUpMessageWithPlaces,
  selectLatestNonFollowUpMessageWithStructuredResults,
  selectLoadedCustomerMessageCount,
  selectMentionedPlaces,
  selectPersistentBotMessages,
  selectPersistentMessages,
  selectPersistentNonFollowUpBotMessagesCount,
  selectStreamIds,
} from "./user-slice"

// Places mentioned in the chat EXCEPT those that have been saved
// or added to the itinerary.
export const selectMentionedPlacesExcludingSavedAndAdded = createSelector(
  [selectMentionedPlaces, selectAddedAndSavedPlaces],
  (allMentioned, addedAndSaved) => {
    return allMentioned.filter(
      (m) => !addedAndSaved.some((p) => p.entity_ref === m.entity_ref)
    )
  }
)

export const selectAllPlaces = createSelector(
  [selectMentionedPlaces, selectTripSavedPlaces, selectAddedPlaces],
  (mentionedPlaces, savedPlaces, addedPlaces) =>
    uniqBy([...mentionedPlaces, ...savedPlaces, ...addedPlaces], "entity_ref")
)

export function filterMarkersByType(
  markers: MapMarker[],
  type: FilteredPlaceType,
  savedRefs: Set<string>,
  addedRefs: Set<string>
) {
  if (!type) return markers

  if (type === "saved") {
    return markers.filter((marker) => {
      const entityRef = mapMarkerEntityRef(marker)
      return entityRef ? savedRefs.has(entityRef) : false
    })
  }

  if (type === "added") {
    return markers.filter((marker) => {
      if (marker.markerType === "itinerary_event") return true
      const entityRef = mapMarkerEntityRef(marker)
      return entityRef ? addedRefs.has(entityRef) : false
    })
  }

  return markers.filter((marker) => {
    switch (marker.markerType) {
      case "place":
        return marker.place.type === type
      case "place_placeholder":
        return marker.placeholder.type === type
      case "itinerary_event":
        return marker.itineraryEventType === (type as string)
      case "web_search_result":
        return marker.mapTarget.type === type
      case "custom_place":
        return false
    }
  })
}

export const selectAllChatMarkers = createSelector(
  [selectAllPlaces, selectLastIntentCode, selectLastBotMessageTypesOfInterest],
  (allPlaces, lastIntentCode, lastBotMessageTypesOfInterest) => {
    const includeAirports =
      lastIntentCode === "F1" ||
      lastBotMessageTypesOfInterest?.includes("airport") ||
      lastBotMessageTypesOfInterest?.includes("flight")

    const placeMarkers = allPlaces
      .filter((place) => includeAirports || !isAirport(place))
      .map(placeToMapMarker)
      .filter(present)

    return placeMarkers
  }
)

const selectItineraryEventMarkers = createSelector(
  [selectItinerary],
  (itinerary) =>
    itinerary.events
      .filter((event) => !isCancelled(event) && !isPlaceItineraryEvent(event))
      .map(itineraryEventToMapMarker)
      .filter(present)
)

const selectDisplayOnlyBaseMarkers = createSelector(
  [selectAllChatMarkers, selectItineraryEventMarkers],
  (allPlaceMarkers, itineraryEventMarkers) => [
    ...allPlaceMarkers,
    ...itineraryEventMarkers,
  ]
)

/** False on the opening view of a chat — nothing has been asked yet. */
export const selectHasCustomerMessages = createSelector(
  [selectPersistentMessages],
  (messages) => messages.some((message) => message.type === "customer")
)

export const selectHasChatPlaces = createSelector(
  [selectDisplayOnlyBaseMarkers],
  (allMarkers) => allMarkers.length > 0
)

/** True when Home guides are cached for the current home location. */
export const selectHasCachedAppHomeGuidesForLocation = createSelector(
  [selectHomeLocation, (state: RootState) => state],
  (location, state) => {
    if (!location) return false
    const { data } = inspirationApi.endpoints.getAppHomeGuides.select(
      location.entity_ref
    )(state)
    return !!data && data.length > 0
  }
)

/** Geo resolving or actively acquiring a fix for app home guides. */
export const selectIsAppHomeGuidesLocationLoading = createSelector(
  [selectIsAutoDetectingLocation, selectIsGeoAcquiring],
  (isResolving, isAcquiring) => isResolving || isAcquiring
)

export const selectShowExamples = createSelector(
  [
    selectEmbedded,
    selectOrganizationExamples,
    selectLoadedCustomerMessageCount,
    selectTripIsUserCreated,
  ],
  (
    embedded,
    organizationExamples,
    totalCustomerMessageCount,
    tripIsUserCreated
  ) =>
    totalCustomerMessageCount === 0 &&
    embedded &&
    !isEmpty(organizationExamples) &&
    !tripIsUserCreated
)

// The whole RTK Query cache now: every endpoint file injects into one api.
const selectApiCache = (state: RootState) => state.api

/**
 * A query has produced a result at least once: data or a rejection. Judged on
 * data presence, not `status`, because a background refetch (e.g. the
 * feature-flag tag invalidated on session refresh) sets `status: "pending"`
 * while keeping `data` — and must not read as "loading again" here, or the
 * map viewport bounces between the location and the markers.
 */
function querySettled(query: { data?: unknown; isError: boolean }) {
  return query.data !== undefined || query.isError
}

/**
 * True when the empty-chat / app-home map has its full marker set: no explore
 * stream is in flight, and for-you ribbon rows have all settled. Used so we
 * fit the viewport once instead of chasing each streaming batch.
 *
 * Deliberately not gated on `loadingResults`: it starts `true` and is only
 * cleared by an explore place fetch, which the homepage no longer runs (its
 * place rows come from the ribbons) — so it would hold the viewport forever.
 */
export const selectHomeMapPlacesReady = createSelector(
  [selectExploreIsStreaming, selectApiCache, selectHomeLocation],
  (isExploreStreaming, apiCache, location) => {
    if (isExploreStreaming) return false

    const locationRef = location?.entity_ref
    if (!locationRef) return true

    const apiState = { api: apiCache }
    const ribbonsQuery = forYouApi.endpoints.getForYouRibbons.select({
      locationRef,
    })(apiState)
    if (!querySettled(ribbonsQuery)) return false

    const ribbons = ribbonsQuery.data?.here ?? []
    for (const ribbon of ribbons) {
      const result = forYouApi.endpoints.getForYouRibbonPlaces.select({
        ref: ribbon.ref,
        locationRef,
      })(apiState)
      if (!querySettled(result)) return false
    }
    return true
  }
)

/**
 * Personalized ribbon places for the current home location, from the RTK
 * cache. Empty until every ribbon row has settled (loaded or failed): rows
 * land a few at a time, and contributing them piecemeal would refit the map
 * viewport once per row.
 */
export const selectForYouRibbonPlaces = createSelector(
  [selectApiCache, selectHomeLocation],
  (apiCache, location): PlaceSummary[] => {
    const locationRef = location?.entity_ref
    if (!locationRef) return []

    const apiState = { api: apiCache }
    // Only the picker-location rows: destination ideas and rows pinned
    // elsewhere would drag the map viewport across the world.
    const ribbons =
      forYouApi.endpoints.getForYouRibbons.select({ locationRef })(apiState)
        .data?.here ?? []

    const places: PlaceSummary[] = []
    for (const ribbon of ribbons) {
      const result = forYouApi.endpoints.getForYouRibbonPlaces.select({
        ref: ribbon.ref,
        locationRef,
      })(apiState)
      if (!querySettled(result)) return []
      // A getaway ring is hours out; its pins would zoom the map out to the state.
      if (ribbon.fits_map) places.push(...(result.data?.places ?? []))
    }
    return uniqBy(places, "entity_ref")
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } }
)

/**
 * The open-web events in the picker's ribbons, as map markers. They are not
 * places and have no entity of their own, so they ride the same path chat uses:
 * each carries its row's places, from which the marker takes the venue's
 * position (ForYou::Ribbons#venue_places sends them for exactly this). Kept off
 * `selectForYouRibbonPlaces` on purpose — these markers never enter the
 * viewport fit, which is what keeps an event's metro-wide search from zooming
 * the map out of the place the rest of the page is about.
 */
export const selectForYouWebSearchResults = createSelector(
  [selectApiCache, selectHomeLocation],
  (apiCache, location): WebSearchResultForMap[] => {
    const locationRef = location?.entity_ref
    if (!locationRef) return []

    const apiState = { api: apiCache }
    const ribbons =
      forYouApi.endpoints.getForYouRibbons.select({ locationRef })(apiState)
        .data?.here ?? []

    const results: WebSearchResultForMap[] = []
    for (const ribbon of ribbons) {
      const result = forYouApi.endpoints.getForYouRibbonPlaces.select({
        ref: ribbon.ref,
        locationRef,
      })(apiState)
      const data = result.data
      if (!querySettled(result) || !data) continue

      data.web_events.forEach((searchResult, index) => {
        results.push({
          messageId: FOR_YOU_WEB_EVENT_SOURCE,
          structuredResult: searchResult.ref ?? `${ribbon.ref}:${index}`,
          searchResult,
          places: data.places,
        })
      })
    }
    return results
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } }
)

/** True when the chat map has no conversation yet (empty chat / app home). */
export const selectIsIdleChatMap = createSelector(
  [selectHasPersistentMessages, selectHasInProgressMessage, selectStreamIds],
  (hasPersistentMessages, hasInProgressMessage, streamIds) =>
    !hasPersistentMessages && !hasInProgressMessage && streamIds.length === 0
)

/**
 * Whether the chat page mounts the map column (split map or XS full-screen map).
 * Non-embedded chats stay map-free until there is at least one pin or a
 * destination is set; embedded chats keep the column.
 */
export const selectChatMapColumnVisible = createSelector(
  [selectEmbedded, selectHasChatPlaces, selectTripHasDestination],
  (embedded, hasChatPlaces, tripHasDestination) =>
    embedded || hasChatPlaces || tripHasDestination
)

/**
 * True when the chat map is showing home-card places (idle chat, nothing of
 * its own to plot) rather than chat, highlighted, or trip markers.
 */
export const selectShowsHomeMapPlaces = createSelector(
  [
    selectDisplayOnlyBaseMarkers,
    selectHighlightedMessage,
    selectForYouRibbonPlaces,
    selectTripMajorStopsDetails,
    selectIsIdleChatMap,
  ],
  (
    allMarkers,
    highlightedMessage,
    homePlaces,
    majorStopsDetails,
    isIdleChatMap
  ) => {
    if (highlightedMessage || !isIdleChatMap) return false

    const hasPlaceMarker = allMarkers.some(isPlaceBasedMarker)
    if (hasPlaceMarker || majorStopsDetails?.length > 0) return false
    return homePlaces.length > 0
  }
)

export const selectDisplayMarkers = createSelector(
  [
    selectDisplayOnlyBaseMarkers,
    selectHighlightedMessage,
    selectHighlightedPlaces,
    selectShowsHomeMapPlaces,
    selectForYouRibbonPlaces,
    selectTripMajorStopsDetails,
  ],
  (
    allMarkers,
    highlightedMessage,
    highlightedPlaces,
    showsHomePlaces,
    homePlaces,
    majorStopsDetails
  ) => {
    if (highlightedMessage) {
      return highlightedPlaces.map(placeToMapMarker).filter(present)
    }

    if (showsHomePlaces) {
      return homePlaces.map(placeToMapMarker).filter(present)
    }

    const hasPlaceMarker = allMarkers.some(isPlaceBasedMarker)
    if (!hasPlaceMarker && majorStopsDetails?.length > 0) {
      return majorStopsDetails
        .map((stop) => placeToMapMarker(stop.location))
        .filter(present)
    }

    return allMarkers
  }
)

export const selectDedupedChatAreaMarkers = createSelector(
  [selectChatAreaPlaces, selectAllPlaces],
  (areaPlaces, chatPlaces) => {
    const chatRefs = new Set(chatPlaces.map((place) => place.entity_ref))
    return areaPlaces
      .filter((place) => !chatRefs.has(place.entity_ref))
      .map(placeToMapMarker)
      .filter(present)
  }
)

export const selectChatAreaMapMarkers = selectDedupedChatAreaMarkers

export const selectChatMapMarkers = createSelector(
  [
    selectDisplayMarkers,
    selectFilteredPlaceType,
    selectTripSavedPlaces,
    selectAddedPlaces,
  ],
  (markers, filteredPlaceType, savedPlaces, addedPlaces) => {
    const savedRefs = new Set(savedPlaces.map((place) => place.entity_ref))
    const addedRefs = new Set(addedPlaces.map((place) => place.entity_ref))
    return filterMarkersByType(markers, filteredPlaceType, savedRefs, addedRefs)
  }
)

export const selectCountByType = createSelector(
  [selectDisplayOnlyBaseMarkers, selectTripSavedPlaces, selectAddedPlaces],
  (baseMarkers, savedPlaces, addedPlaces) => (type: FilteredPlaceType) => {
    const savedRefs = new Set(savedPlaces.map((place) => place.entity_ref))
    const addedRefs = new Set(addedPlaces.map((place) => place.entity_ref))
    return filterMarkersByType(baseMarkers, type, savedRefs, addedRefs).length
  }
)

export const selectFilteredPlaces = createSelector(
  [selectChatMapMarkers],
  (markers) =>
    markers
      .filter((marker) => marker.markerType === "place")
      .map((marker) => marker.place)
)

const EMPTY_PLACES: PlaceSummary[] = []

/**
 * Whether the chat map is on screen. It stays mounted behind the chat and is
 * only hidden with a CSS class, so a map that is alive and reporting bounds
 * still says nothing about whether the user can see it.
 */
export const selectChatMapVisible = createSelector(
  [selectIsSplitView, selectShowSmallUIMap],
  (isSplitView, showSmallUIMap) => isSplitView || showSmallUIMap
)

/**
 * Places of the latest reply as the chat map shows them: the filtered markers
 * narrowed to that message, minus the trip destination once the chat has moved
 * past its opener, minus geographic outliers.
 */
export const selectLatestMessageMapPlaces = createSelector(
  [
    selectFilteredPlaces,
    selectLatestNonFollowUpMessageWithPlaces,
    selectTripDestination,
    selectPersistentNonFollowUpBotMessagesCount,
  ],
  (filteredPlaces, latestMessage, destination, nonFollowUpBotMessageCount) => {
    if (!latestMessage) return EMPTY_PLACES

    let places = filteredPlaces.filter((place) =>
      latestMessage.places.some((e) => e.entity_ref === place.entity_ref)
    )

    if (
      destination &&
      filteredPlaces.length > 1 &&
      nonFollowUpBotMessageCount > 1
    ) {
      const withoutDestination = places.filter(
        (place) => place.entity_ref !== destination.entity_ref
      )
      // A lone location left behind (the origin of a flight to the
      // destination) would be fit as a single pin at street zoom. Keep the
      // destination so the map frames both.
      const leavesLoneLocation =
        withoutDestination.length === 1 &&
        withoutDestination.length < places.length &&
        isLocation(withoutDestination[0])
      if (!leavesLoneLocation) places = withoutDestination
    }

    const outlierFilteredEntities = filterOutlierEntities(latestMessage)
    return places.filter((place) =>
      outlierFilteredEntities.some((e) => e.entity_ref === place.entity_ref)
    )
  }
)

export const selectHasFilteredPlaces = createSelector(
  [selectChatMapMarkers],
  (markers) => markers.length > 0
)

export const selectFilteredPlacesCount = createSelector(
  [selectChatMapMarkers],
  (markers) => markers.length
)

export const selectFirstFilteredPlace = createSelector(
  [selectFilteredPlaces],
  (filteredPlaces) => filteredPlaces[0]
)

// Entity refs that have an associated hotel price quote in any message.
// Used to suppress the live map marker price when a quote already provides pricing context.
export const selectQuotedHotelEntityRefs = createSelector(
  [selectPersistentMessages, selectInProgressMessage],
  (persistentMessages, inProgressMessage) => {
    const refs = new Set<string>()
    const allMessages = inProgressMessage
      ? [...persistentMessages, inProgressMessage]
      : persistentMessages
    for (const message of allMessages) {
      for (const marker of message.markers) {
        if (marker.type !== "card") continue
        if (marker.quote_ref && marker.ref) {
          refs.add(marker.ref)
        }
      }
    }
    return refs
  }
)

/** Base chat markers visible during an active area search (saved, added, quoted hotels). */
export const selectAreaSearchChatMapMarkers = createSelector(
  [
    selectDisplayMarkers,
    selectTripSavedPlaces,
    selectAddedPlaces,
    selectQuotedHotelEntityRefs,
  ],
  (markers, savedPlaces, addedPlaces, quotedHotelRefs) => {
    const savedRefs = new Set(savedPlaces.map((place) => place.entity_ref))
    const addedRefs = new Set(addedPlaces.map((place) => place.entity_ref))
    return markers.filter((marker) => {
      if (marker.markerType === "itinerary_event") return true
      const entityRef = mapMarkerEntityRef(marker)
      if (!entityRef) return false
      return (
        savedRefs.has(entityRef) ||
        addedRefs.has(entityRef) ||
        quotedHotelRefs.has(entityRef)
      )
    })
  }
)

// All quote_refs across all messages in the chat (uses the new quote_refs field on messages).
export const selectAllQuoteRefs = createSelector(
  [selectPersistentMessages, selectInProgressMessage],
  (persistentMessages, inProgressMessage) => {
    const refs = new Set<string>()
    const allMessages = inProgressMessage
      ? [...persistentMessages, inProgressMessage]
      : persistentMessages
    for (const message of allMessages) {
      if (message.quote_refs) {
        for (const ref of message.quote_refs) {
          refs.add(ref)
        }
      }
    }
    return refs
  }
)

// Map from entity_ref to quote_ref, built from card markers.
// When multiple quotes exist for the same entity, the last one wins (most recent in message order).
export const selectQuoteRefByEntityRef = createSelector(
  [selectPersistentMessages, selectInProgressMessage],
  (persistentMessages, inProgressMessage) => {
    const map = new Map<string, string>()
    const allMessages = inProgressMessage
      ? [...persistentMessages, inProgressMessage]
      : persistentMessages
    for (const message of allMessages) {
      for (const marker of message.markers) {
        if (marker.type !== "card") continue
        if (marker.quote_ref && marker.ref) {
          map.set(marker.ref, marker.quote_ref)
        }
      }
    }
    return map
  }
)

// for google viewport setting
export const selectMapLocation = createSelector(
  [
    selectFilteredPlacesCount,
    selectFirstFilteredPlace,
    selectTripDestination,
    selectLatestMessagePlaceCount,
    selectFirstLatestMessagePlace,
    selectHasChatPlaces,
    selectHomeLocation,
    selectHomeMapPlacesReady,
  ],
  (
    filteredPlacesCount,
    firstFilteredPlace,
    destination,
    latestEntitiesCount,
    firstLatestMessageEntity,
    hasChatPlaces,
    homeLocation,
    homeMapPlacesReady
  ) => {
    // no places, show destination bounds
    if (filteredPlacesCount === 0) {
      if (destination) {
        return destination
      } else if (!hasChatPlaces) {
        return homeLocation
      }
    }

    // single location place
    if (latestEntitiesCount === 1 && isLocation(firstLatestMessageEntity)) {
      return firstLatestMessageEntity
    }

    if (
      filteredPlacesCount === 1 &&
      firstFilteredPlace &&
      isLocation(firstFilteredPlace)
    ) {
      return firstFilteredPlace
    }

    // Home-card / nearby places still loading. Keep the user's selected
    // location as the viewport until the full marker set is in, then fit
    // once — so we don't zoom in on the first few and out again as the
    // rest arrive.
    if (!hasChatPlaces && homeLocation && !homeMapPlacesReady) {
      return homeLocation
    }

    return undefined
  }
)

export interface ChatAddressAnchor {
  streetAddress: string
  latitude: number
  longitude: number
}

function addressAnchorFromIntent(
  intent: Intent | undefined
): ChatAddressAnchor | undefined {
  if (
    !intent?.street_address ||
    intent.latitude == null ||
    intent.longitude == null
  ) {
    return undefined
  }

  return {
    streetAddress: intent.street_address,
    latitude: intent.latitude,
    longitude: intent.longitude,
  }
}

function addressAnchorFromMessage(
  message: PersistentMessage | InProgressMessage | undefined
): ChatAddressAnchor | undefined {
  if (!message || message.type !== "bot") return undefined
  return addressAnchorFromIntent(message.intent)
}

function latestAddressAnchorFromMessages(
  messages: readonly (PersistentMessage | InProgressMessage)[]
): ChatAddressAnchor | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const anchor = addressAnchorFromMessage(messages[i])
    if (anchor) return anchor
  }
  return undefined
}

export const selectFocusedChatAddressAnchor = createSelector(
  [
    selectHighlightedMessage,
    selectInProgressMessage,
    selectLatestNonFollowUpMessageWithPlaces,
  ],
  (highlightedMessage, inProgressMessage, latestMessageWithPlaces) => {
    const focusedMessage =
      highlightedMessage ?? inProgressMessage ?? latestMessageWithPlaces
    return addressAnchorFromMessage(focusedMessage)
  }
)

/**
 * Routes a search-along-route answer searched along, from the same message
 * whose places the map shows: the highlighted one, else the latest answer
 * with places. An older message's route never lingers under newer pins.
 */
export const selectChatMapRoutes = createSelector(
  [selectHighlightedMessage, selectLatestNonFollowUpMessageWithPlaces],
  (highlightedMessage, latestMessageWithPlaces) =>
    (highlightedMessage ?? latestMessageWithPlaces)?.structured_results?.routes
)

export const selectChatAddressAnchor = createSelector(
  [
    selectHighlightedMessage,
    selectInProgressMessage,
    selectPersistentBotMessages,
  ],
  (highlightedMessage, inProgressMessage, persistentBotMessages) => {
    if (highlightedMessage) {
      return addressAnchorFromMessage(highlightedMessage)
    }

    const inProgressAnchor = addressAnchorFromMessage(inProgressMessage)
    if (inProgressAnchor) return inProgressAnchor

    return latestAddressAnchorFromMessages(persistentBotMessages)
  }
)

export const selectWebSearchResultsForMap = createSelector(
  [
    selectLatestNonFollowUpMessageWithStructuredResults,
    selectHighlightedMessage,
    selectInProgressMessage,
  ],
  (latestMessage, highlightedMessage, inProgressMessage) => {
    const primaryMessage =
      highlightedMessage && messageHasWebSearchResultsForMap(highlightedMessage)
        ? highlightedMessage
        : latestMessage

    const results = primaryMessage
      ? collectWebSearchResultsFromMessage(primaryMessage)
      : []

    if (
      inProgressMessage &&
      inProgressMessage.id !== primaryMessage?.id &&
      messageHasWebSearchResultsForMap(inProgressMessage)
    ) {
      results.push(...collectWebSearchResultsFromMessage(inProgressMessage))
    }

    return results
  }
)
